import '@gnist/design-system/fonts/audi';
import '@gnist/design-system/fonts/autoria';
import '@gnist/design-system/fonts/bilhold';
import '@gnist/design-system/fonts/cupra';
import '@gnist/design-system/fonts/dahles';
import '@gnist/design-system/fonts/gumpen';
import '@gnist/design-system/fonts/moller';
import '@gnist/design-system/fonts/skoda';
import '@gnist/design-system/fonts/VW';
import type { LogoBrand } from '@gnist/design-system/foundation/logos/types.js';
import { audi } from '@gnist/themes/themes/audi.css.js';
import { autoria } from '@gnist/themes/themes/autoria.css.js';
import { bilholdLight } from '@gnist/themes/themes/bilholdLight.css.js';
import { cupra } from '@gnist/themes/themes/cupra.css.js';
import { dahles } from '@gnist/themes/themes/dahles.css.js';
import { gumpen } from '@gnist/themes/themes/gumpen.css.js';
import { mollerBil } from '@gnist/themes/themes/mollerBil.css.js';
import { skoda } from '@gnist/themes/themes/skoda.css.js';
import { vw } from '@gnist/themes/themes/vw.css.js';
import { globalTextStyles } from '@gnist/themes/typography.css.js';
import type React from 'react';
import {
    createContext,
    type ReactNode,
    useContext,
    useLayoutEffect,
    useState,
} from 'react';

const carThemes = ['skoda', 'vw', 'nytte', 'audi', 'cupra'] as const;
export const dealerThemes = [
    'gumpen',
    'dahles',
    'autoria',
    'mollerbil',
    'bilhold',
] as const;
const themes = [...carThemes, ...dealerThemes];

export function getThemeFromLocalStorage() {
    return getThemeFromDealerName(
        window.localStorage.getItem('preferredDealerName') ?? '',
    );
}

export function getThemeFromDealerName(dealerName: string): LogoBrand {
    const normalized = (() => {
        let x = dealerName.toLowerCase();
        x = x.replace('ø', 'o');
        x = x.includes('erik arnesen') ? 'moller' : x;
        x = x.includes('moller') ? 'mollerbil' : x;
        x = x.includes('grenland') ? 'gumpen' : x;
        x = x.includes('autosenteret notodden') ? 'gumpen' : x;
        x = x.includes('svebjorn') ? 'svebjorn' : x;
        x = x.includes('autoria') ? 'autoria' : x;
        x = x.includes('dahles') ? 'dahles' : x;
        x = x.includes('gumpen') ? 'gumpen' : x;
        x = x.includes('volkswagen') ? 'vw' : x;
        x = x.includes('volkswagencommercialvehicles') ? 'nytte' : x;
        return x;
    })();

    const dealerWithBranding = ((n: string): n is LogoBrand =>
        themes.some((x) => x === n))(normalized);
    if (dealerWithBranding) {
        return normalized;
    }
    return 'bilhold';
}

export function getThemeFromBrand(brand: string): LogoBrand {
    const normalized = brand.toLowerCase();
    const brandIsLogoBrand = ((n: string): n is LogoBrand =>
        themes.some((x) => x === n))(normalized);
    if (brandIsLogoBrand) {
        return normalized;
    }
    return 'bilhold';
}

const getCssThemeClass = (theme: string) => {
    switch (theme) {
        case 'mollerbil':
            return mollerBil;
        case 'gumpen':
            return gumpen;
        case 'skoda':
            return skoda;
        case 'vw':
        case 'nytte':
            return vw;
        case 'dahles':
            return dahles;
        case 'autoria':
            return autoria;
        case 'audi':
            return audi;
        case 'cupra':
            return cupra;
        case 'bilhold':
        default:
            return bilholdLight;
    }
};

const ThemeContext = createContext<
    | {
          theme: LogoBrand;
          setTheme: (theme: LogoBrand) => void;
      }
    | undefined
>(undefined);

export const useThemeContext = () => {
    const ctx = useContext(ThemeContext);

    if (!ctx) {
        throw new Error(
            'useThemeContext must be used within a ThemeContextProvider',
        );
    }

    return ctx;
};

export function useUpdateThemeOnMount(theme: LogoBrand | undefined) {
    const { setTheme } = useThemeContext();

    useLayoutEffect(() => {
        if (theme) {
            setTheme(theme);
        }
    }, [setTheme, theme]);
}

export const ThemeContextProvider: React.FC<{
    defaultTheme: LogoBrand;
    overrideTheme?: LogoBrand | null;
    children?: ReactNode;
}> = ({ defaultTheme, overrideTheme, children }) => {
    const [themeState, setThemeState] = useState<LogoBrand>(
        overrideTheme ?? defaultTheme,
    );

    useLayoutEffect(() => {
        document.body.classList.add(getCssThemeClass(themeState));
        return () => {
            document.body.classList.remove(getCssThemeClass(themeState));
        };
    }, [themeState]);

    // Use this to set theme on event
    function setTheme(theme: LogoBrand) {
        if (overrideTheme) {
            return;
        }
        setThemeState(theme);
    }

    // Add text style classes
    useLayoutEffect(() => {
        globalTextStyles.forEach((c) => {
            document.body.classList.add(c);
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme: themeState, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const ChildThemeProvider: React.FC<{
    theme: LogoBrand | null;
    overrideTheme?: LogoBrand | null;
    children?: ReactNode;
}> = ({ theme, overrideTheme, children }) => {
    const parentThemeState = useThemeContext();
    const { setTheme } = parentThemeState;

    useLayoutEffect(() => {
        if (theme !== null) {
            setTheme(theme);
        }

        return () => {
            setTheme(getThemeFromLocalStorage());
        };
    }, [setTheme, theme]);

    const selectedTheme = overrideTheme || theme || parentThemeState.theme;

    const state = {
        theme: selectedTheme,
        setTheme: parentThemeState.setTheme,
    };

    return (
        <ThemeContext.Provider value={state}>{children}</ThemeContext.Provider>
    );
};

export const ThemeWrapper: React.FC<{
    theme: LogoBrand;
    children: React.ReactNode;
}> = ({ theme, children }) => {
    const cls = getCssThemeClass(theme);
    return <div className={cls}>{children}</div>;
};
